import { create } from 'zustand';
import type { AlertEvent } from '../hooks/useMonitoringAlerts';
import { parseUtcTimestampMs } from '../utils/timeUtils';

export interface CrossZoneAlertEntry {
  /** Dedup key: ${type}_${zoneId}_${elementId}_${timestamp} */
  key: string;
  type: 'trafo_alert' | 'voltage_alert' | 'cable_alert';
  zoneId: string;
  zoneName: string;
  elementId: string | null;
  previousState: string | null;
  newState: string;
  metricValue: number;
  thresholdValue: number | null;
  source: string | null;
  triggerType: string | null;
  phase: string | null;
  elementType?: string | null;
  elementName?: string | null;
  timestamp: string;
  receivedAt: number;
  /** Set when a GREEN recovery arrives for the same type+zone+element */
  resolvedAt: string | null;
}

const MAX_HISTORY = 100;

function makeDedupeKey(alert: AlertEvent): string {
  const d = alert.data;
  const elementId = 'elementId' in d ? d.elementId : null;
  return `${alert.type}_${d.zoneId}_${elementId}_${d.timestamp}`;
}

function resolveZoneName(data: { zoneId: string }): string {
  const rec = data as Record<string, unknown>;
  return (typeof rec.zoneName === 'string') ? rec.zoneName : data.zoneId;
}

function alertEventToEntry(alert: AlertEvent): CrossZoneAlertEntry {
  if (alert.type === 'trafo_alert') {
    const d = alert.data;
    return {
      key: makeDedupeKey(alert),
      type: 'trafo_alert',
      zoneId: d.zoneId,
      zoneName: resolveZoneName(d),
      elementId: d.elementId,
      previousState: d.previousState,
      newState: d.newState,
      metricValue: d.loadingPercent,
      thresholdValue: d.thresholdPercent,
      source: d.source,
      triggerType: null,
      phase: null,
      timestamp: d.timestamp,
      receivedAt: Date.now(),
      resolvedAt: null,
    };
  } else if (alert.type === 'cable_alert') {
    const d = alert.data;
    return {
      key: makeDedupeKey(alert),
      type: 'cable_alert',
      zoneId: d.zoneId,
      zoneName: resolveZoneName(d),
      elementId: d.elementId,
      previousState: d.previousState,
      newState: d.newState,
      metricValue: d.loadingPercent,
      thresholdValue: d.thresholdPercent,
      source: d.source,
      triggerType: null,
      phase: null,
      timestamp: d.timestamp,
      receivedAt: Date.now(),
      resolvedAt: null,
    };
  } else {
    const d = alert.data;
    return {
      key: makeDedupeKey(alert),
      type: 'voltage_alert',
      zoneId: d.zoneId,
      zoneName: resolveZoneName(d),
      elementId: d.elementId ?? null,
      previousState: d.previousState,
      newState: d.newState,
      metricValue: d.voltagePu,
      thresholdValue: d.thresholdPu,
      source: null,
      triggerType: d.triggerType,
      phase: d.phase ?? null,
      elementType: d.elementType ?? null,
      elementName: d.elementName ?? null,
      timestamp: d.timestamp,
      receivedAt: Date.now(),
      resolvedAt: null,
    };
  }
}

interface AlertState {
  /** Latest SSE alert event — subscribers react to changes (existing) */
  lastAlert: AlertEvent | null;
  setLastAlert: (alert: AlertEvent) => void;

  /** Cross-zone alert history (ring buffer, last 100) */
  alertHistory: CrossZoneAlertEntry[];
  /** Add alert with dedup by composite key. GREEN states are ignored. */
  addAlert: (alert: AlertEvent) => void;

  /** Unread badge count */
  unreadCount: number;
  /** Keys of alerts the user has already seen */
  readKeys: Set<string>;
  /** Mark a single alert as read */
  markRead: (key: string) => void;
  markAllRead: () => void;

  /** Notification center panel toggle */
  isNotificationCenterOpen: boolean;
  setNotificationCenterOpen: (open: boolean) => void;

  /** Backfill from REST history endpoint */
  backfillHistory: (entries: CrossZoneAlertEntry[]) => void;
}

export const useAlertStore = create<AlertState>()((set, get) => ({
  lastAlert: null,
  setLastAlert: (alert) => set({ lastAlert: alert }),

  alertHistory: [],
  addAlert: (alert) => {
    const d = alert.data;
    const elementId = 'elementId' in d ? d.elementId : null;

    // GREEN recovery: mark the most recent matching hazardous entry as resolved
    if (d.newState === 'GREEN') {
      const { alertHistory } = get();
      const matchIdx = alertHistory.findIndex(
        e => e.type === alert.type
          && e.zoneId === d.zoneId
          && e.elementId === elementId
          && !e.resolvedAt,
      );
      if (matchIdx >= 0) {
        const updated = [...alertHistory];
        updated[matchIdx] = { ...updated[matchIdx], resolvedAt: d.timestamp };
        set({ alertHistory: updated });
      }
      return;
    }

    const entry = alertEventToEntry(alert);
    const { alertHistory } = get();

    // Dedup: skip if key already exists
    if (alertHistory.some(e => e.key === entry.key)) {
      return;
    }

    const updated = [entry, ...alertHistory].slice(0, MAX_HISTORY);
    set(state => ({
      alertHistory: updated,
      unreadCount: state.unreadCount + 1,
    }));
  },

  unreadCount: 0,
  readKeys: new Set<string>(),
  markRead: (key) => {
    const { readKeys, unreadCount } = get();
    if (readKeys.has(key)) return;
    const next = new Set(readKeys);
    next.add(key);
    set({ readKeys: next, unreadCount: Math.max(0, unreadCount - 1) });
  },
  markAllRead: () => set(state => {
    const allKeys = new Set(state.alertHistory.map(e => e.key));
    return { unreadCount: 0, readKeys: allKeys };
  }),

  isNotificationCenterOpen: false,
  setNotificationCenterOpen: (open) => set({ isNotificationCenterOpen: open }),

  backfillHistory: (entries) => {
    const { alertHistory } = get();
    const existingKeys = new Set(alertHistory.map(e => e.key));
    const newEntries = entries.filter(e => !existingKeys.has(e.key));
    if (newEntries.length === 0) return;

    const merged = [...alertHistory, ...newEntries]
      .sort((a, b) => parseUtcTimestampMs(b.timestamp) - parseUtcTimestampMs(a.timestamp))
      .slice(0, MAX_HISTORY);
    set({ alertHistory: merged });
  },
}));
